import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import authService from "../services/authService";
import { LOGO_SRC } from "../constants/branding";
import { normalizeAuthApiErrors } from "../utils/normalizeAuthApiErrors";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const uid = useMemo(() => params.get("uid") || "", [params]);
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Both pieces are required for a valid reset link. Render an error state up
  // front rather than letting the user submit only to fail on the backend.
  const linkInvalid = !uid || !token;

  useEffect(() => {
    if (linkInvalid) {
      setErrors({
        submit:
          "This reset link is missing required information. Request a new one from the forgot-password page.",
      });
    }
  }, [linkInvalid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (linkInvalid) return;

    const next = {};
    if (!password) next.password = "Password is required";
    else if (password.length < 8)
      next.password = "Password must be at least 8 characters long";
    if (!passwordConfirm) next.passwordConfirm = "Please confirm your password";
    else if (password !== passwordConfirm)
      next.passwordConfirm = "Passwords do not match";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setIsLoading(true);
    try {
      await authService.confirmPasswordReset(uid, token, password, passwordConfirm);
      setDone(true);
      toast.success("Password reset. You can sign in now.");
      // Soft auto-redirect — the explicit CTA below also navigates immediately.
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const normalized = normalizeAuthApiErrors(err.response?.data);
      const detail =
        normalized.submit ||
        err.response?.data?.detail ||
        err.message ||
        "Could not reset your password.";
      setErrors({ ...normalized, submit: detail });
      toast.error(detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="rounded-2xl shadow-xl bg-[#48484826] p-8 sm:p-10">
            <div className="mb-8 text-center">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-16"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                Choose a new password
              </h2>
              <p className="font-secondary mt-2 text-[17px] leading-relaxed text-white/65">
                Pick something at least 8 characters long.
              </p>
            </div>

            {done ? (
              <div
                className="font-secondary rounded-sm border border-emerald-500/25 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-200"
                role="status"
              >
                <p className="font-semibold">Password reset.</p>
                <p className="mt-1 text-emerald-200/85">
                  You can now sign in with your new password.{" "}
                  <Link to="/login" className="text-emerald-100 underline hover:text-white">
                    Go to sign in
                  </Link>
                </p>
              </div>
            ) : (
              <>
                {errors.submit && (
                  <div
                    className="font-secondary mb-6 rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    role="alert"
                  >
                    {errors.submit}
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label
                      htmlFor="password"
                      className="font-secondary mb-2 block text-sm font-medium text-white/80"
                    >
                      New password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setErrors((prev) => ({ ...prev, password: undefined, submit: undefined }));
                      }}
                      required
                      autoComplete="new-password"
                      className={`font-secondary block w-full rounded-sm border px-4 py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1 ${
                        errors.password
                          ? "border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30"
                          : "border-[#4848481A] bg-[#1F1F1F] hover:border-white/25 focus:border-primary focus:ring-primary/30"
                      }`}
                      placeholder="At least 8 characters"
                      disabled={linkInvalid}
                    />
                    {errors.password && (
                      <p className="mt-2 text-sm text-red-400">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="passwordConfirm"
                      className="font-secondary mb-2 block text-sm font-medium text-white/80"
                    >
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      id="passwordConfirm"
                      name="passwordConfirm"
                      value={passwordConfirm}
                      onChange={(e) => {
                        setPasswordConfirm(e.target.value);
                        setErrors((prev) => ({
                          ...prev,
                          passwordConfirm: undefined,
                          submit: undefined,
                        }));
                      }}
                      required
                      autoComplete="new-password"
                      className={`font-secondary block w-full rounded-sm border px-4 py-3.5 text-[15px] text-white transition-colors placeholder:text-white/40 focus:outline-none focus:ring-1 ${
                        errors.passwordConfirm
                          ? "border-red-500/80 bg-red-500/10 focus:border-red-500 focus:ring-red-500/30"
                          : "border-[#4848481A] bg-[#1F1F1F] hover:border-white/25 focus:border-primary focus:ring-primary/30"
                      }`}
                      placeholder="Confirm your new password"
                      disabled={linkInvalid}
                    />
                    {errors.passwordConfirm && (
                      <p className="mt-2 text-sm text-red-400">{errors.passwordConfirm}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isLoading || linkInvalid}
                    className="font-secondary mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                    whileHover={isLoading || linkInvalid ? {} : { scale: 1.01 }}
                    whileTap={isLoading || linkInvalid ? {} : { scale: 0.99 }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#0a0a0a] border-t-transparent"
                          aria-hidden="true"
                        />
                        Updating…
                      </>
                    ) : (
                      "Update password"
                    )}
                  </motion.button>
                </form>

                {linkInvalid && (
                  <p className="font-secondary mt-6 text-center text-sm text-white/65">
                    <Link
                      to="/forgot-password"
                      className="font-semibold text-primary transition-colors hover:text-primary-hover"
                    >
                      Request a new reset link
                    </Link>
                  </p>
                )}
              </>
            )}

            <div className="mt-8 text-center">
              <p className="font-secondary text-[15px] text-white/65">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-primary transition-colors hover:text-primary-hover"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
